import { useState } from "react";
import { Link } from "react-router-dom";
import { Instagram, Facebook, Linkedin, MapPin, Mail, Phone } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { SOCIALS } from "@/i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Footer = ({ t }) => {
  const f = t.footer;
  const [email, setEmail] = useState("");
  const [sending, setSending] = useState(false);

  const subscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    setSending(true);
    try {
      await axios.post(`${API}/newsletter`, { email });
      toast.success(f.subscribed);
      setEmail("");
    } catch {
      toast.error(t.contactPage.form.error);
    } finally {
      setSending(false);
    }
  };

  const explore = [
    { to: "/", label: t.nav.home },
    { to: "/menu", label: t.nav.menu },
    { to: "/breakfast", label: t.nav.breakfast },
    { to: "/lunch", label: t.nav.lunch },
    { to: "/sweets", label: t.nav.sweets },
    { to: "/catering", label: t.nav.catering },
    { to: "/story", label: t.nav.story },
    { to: "/contact", label: t.nav.contact },
  ];

  return (
    <footer className="bg-navy text-cream" data-testid="footer-section">
      <div className="mx-auto grid max-w-7xl grid-cols-1 gap-10 px-4 py-14 sm:grid-cols-2 md:px-8 lg:grid-cols-4">
        <div>
          <div className="flex items-center gap-2.5">
            <img src="/logo.png" alt="the Kurkuma logo" className="h-11 w-auto" data-testid="footer-logo" />
            <span className="leading-tight">
              <span className="block font-display text-lg font-bold tracking-wide">KURKUMA</span>
              <span className="block text-[10px] font-medium tracking-[0.35em] text-brand">AI KITCHEN</span>
            </span>
          </div>
          <p className="mt-4 text-sm leading-relaxed text-cream/60">{f.tagline}</p>
          <div className="mt-5 flex gap-3">
            <a href={SOCIALS.instagram} target="_blank" rel="noopener noreferrer" aria-label="Instagram" data-testid="footer-instagram"
              className="flex h-9 w-9 items-center justify-center rounded-full border border-white/15 text-cream/70 transition-colors hover:border-brand hover:text-brand">
              <Instagram className="h-4 w-4" />
            </a>
            <a href={SOCIALS.facebook} target="_blank" rel="noopener noreferrer" aria-label="Facebook" data-testid="footer-facebook"
              className="flex h-9 w-9 items-center justify-center rounded-full border border-white/15 text-cream/70 transition-colors hover:border-brand hover:text-brand">
              <Facebook className="h-4 w-4" />
            </a>
            <a href={SOCIALS.linkedin} target="_blank" rel="noopener noreferrer" aria-label="LinkedIn" data-testid="footer-linkedin"
              className="flex h-9 w-9 items-center justify-center rounded-full border border-white/15 text-cream/70 transition-colors hover:border-brand hover:text-brand">
              <Linkedin className="h-4 w-4" />
            </a>
          </div>
        </div>

        <div>
          <h3 className="mb-4 font-display text-sm font-semibold uppercase tracking-widest text-brand">{f.explore}</h3>
          <ul className="space-y-2.5 text-sm text-cream/70">
            {explore.map((l) => (
              <li key={l.to}>
                <Link to={l.to} className="transition-colors hover:text-brand">{l.label}</Link>
              </li>
            ))}
          </ul>
        </div>

        <div>
          <h3 className="mb-4 font-display text-sm font-semibold uppercase tracking-widest text-brand">{f.contactCol}</h3>
          <ul className="space-y-3 text-sm text-cream/70">
            <li className="flex items-start gap-2.5" data-testid="footer-address">
              <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-brand" /> {f.address}
            </li>
            <li>
              <a href={`mailto:${f.email}`} className="flex items-center gap-2.5 transition-colors hover:text-brand" data-testid="footer-email">
                <Mail className="h-4 w-4 shrink-0 text-brand" /> {f.email}
              </a>
            </li>
            <li>
              <a href={`tel:${f.phone.replace(/\s/g, "")}`} className="flex items-center gap-2.5 transition-colors hover:text-brand" data-testid="footer-phone">
                <Phone className="h-4 w-4 shrink-0 text-brand" /> {f.phone}
              </a>
            </li>
          </ul>
        </div>

        <div>
          <h3 className="mb-4 font-display text-sm font-semibold uppercase tracking-widest text-brand">{f.newsletterTitle}</h3>
          <form onSubmit={subscribe} className="flex flex-col gap-3" data-testid="newsletter-form">
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={f.newsletterPh}
              data-testid="newsletter-email"
              className="rounded-full border border-white/15 bg-white/5 px-5 py-2.5 text-sm text-cream placeholder:text-cream/40 outline-none transition-colors focus:border-brand"
            />
            <button
              type="submit"
              disabled={sending}
              data-testid="newsletter-submit"
              className="rounded-full bg-brand px-5 py-2.5 text-sm font-semibold text-navy transition-colors hover:bg-branddark disabled:opacity-60"
            >
              {f.subscribe}
            </button>
          </form>
        </div>
      </div>

      <div className="border-t border-white/10">
        <div className="mx-auto flex max-w-7xl flex-col items-center justify-between gap-3 px-4 py-5 text-xs text-cream/40 md:flex-row md:px-8">
          <span>{f.rights}</span>
          <span className="flex gap-5">
            <Link to="/contact" className="transition-colors hover:text-brand">{f.privacy}</Link>
            <Link to="/contact" className="transition-colors hover:text-brand">{f.terms}</Link>
          </span>
        </div>
      </div>
    </footer>
  );
};
